import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { Identicon } from '../components/Identicon.js';
import { GildedImage } from '../components/GildedImage.js';
import { Reveal } from '../components/Reveal.js';

interface Entry {
  address: string;
  score: number;
  displayName: string | null;
  topGenerationId: string | null;
  image: string | null;
  stylePreset: string | null;
}

/** Gradient-filled numeral text. `background-image` (not the shorthand) so
    the text clip survives; the shorthand resets background-clip. */
function gildedNumeral(gradient: string): CSSProperties {
  return {
    backgroundImage: gradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };
}

/* ------------------------------------------------------------------
   Address utilities — deterministic identicon fallback when no ENS.
   Tiny 4x4 gold-on-parchment glyph keyed off the address bytes.
------------------------------------------------------------------ */

function shortAddr(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/* Identicon now lives in components/Identicon.tsx — the canonical 5x5
   gold-on-parchment glyph used across Feed, Remix, and here. */

/* ------------------------------------------------------------------
   Inline ornaments
------------------------------------------------------------------ */


/**
 * Fleuron — tiny four-petal mark used as ornament around the
 * editorial empty state.
 */
function Fleuron({ className }: { className?: string }) {
  const CLS = className ?? 'h-3.5 w-6';
  return (
    <svg viewBox="0 0 28 16" aria-hidden="true" className={CLS} >
      <defs>
        <linearGradient id="ornRamp" x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor="#a87f2c" />
          <stop offset="0.42" stopColor="#e9c873" />
          <stop offset="0.52" stopColor="#fff3ce" />
          <stop offset="0.64" stopColor="#c2922f" />
          <stop offset="1" stopColor="#7d521a" />
        </linearGradient>
      </defs>
      {/* slender cut lozenge with a bright facet seam */}
      <path d="M2 8 L14 3.2 L26 8 L14 12.8 Z" fill="url(#ornRamp)" />
      <path d="M2 8 L26 8" stroke="#fff3ce" strokeWidth="0.5" opacity="0.65" />
      <circle cx="14" cy="8" r="1.1" fill="#fff3ce" />
    </svg>
  );
}

/* ------------------------------------------------------------------
   Pieces
------------------------------------------------------------------ */

/** A struck place medal — a gold / silver / bronze coin with the rank. Reads
 *  instantly as 1st/2nd/3rd, and sits in the brand's metal vocabulary. */
const MEDAL_RAMP: Record<1 | 2 | 3, string> = {
  1: 'linear-gradient(145deg,#fff3ce 0%,#ecca78 32%,#c2922f 64%,#8a6020 100%)',
  2: 'linear-gradient(145deg,#f3f4f7 0%,#cdd1d9 34%,#9aa1ad 66%,#717885 100%)',
  3: 'linear-gradient(145deg,#f1c79a 0%,#d99c60 38%,#ab6c33 70%,#7c4c20 100%)',
};

function RankMedal({ place, size = 48 }: { place: 1 | 2 | 3; size?: number }) {
  const ramp = MEDAL_RAMP[place];
  const inner = Math.round(size * 0.8);
  return (
    <span
      className="relative inline-flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: ramp,
        boxShadow:
          '0 6px 16px -6px rgb(0 0 0 / 0.55), inset 0 1px 1px rgb(255 255 255 / 0.55), inset 0 -2px 3px rgb(0 0 0 / 0.3)',
      }}
      aria-label={`${place === 1 ? '1st' : place === 2 ? '2nd' : '3rd'} place`}
    >
      <span
        className="flex items-center justify-center rounded-full"
        style={{ width: inner, height: inner, background: 'radial-gradient(circle at 38% 32%, #1b1710, #0b0906)' }}
      >
        <span
          className="font-display font-semibold leading-none"
          style={{
            fontSize: size * 0.42,
            backgroundImage: ramp,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          {place}
        </span>
      </span>
    </span>
  );
}

/** The piece itself in a gold plate — falls back to the address glyph if a
 *  leader somehow has no public top piece. Links through to the full gilding. */
function GildPlate({
  entry,
  className,
  ringStrong,
}: {
  entry: Entry;
  className?: string;
  ringStrong?: boolean;
}) {
  const inner = entry.image ? (
    <GildedImage
      src={entry.image}
      alt="leading gilding"
      className="block h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
    />
  ) : (
    <div className="flex h-full w-full items-center justify-center bg-surface-sunken">
      <Identicon address={entry.address} size={56} />
    </div>
  );
  const plate = (
    <div
      className={`plate-lift relative overflow-hidden rounded-lg ${className ?? ''}`}
      style={{
        aspectRatio: '4 / 5',
        border: ringStrong
          ? '1.5px solid rgb(233 200 115 / 0.65)'
          : '1px solid rgb(233 200 115 / 0.38)',
        boxShadow: '0 22px 55px -18px rgb(0 0 0 / 0.55)',
      }}
    >
      {inner}
    </div>
  );
  return entry.topGenerationId ? (
    <Link to={`/result/${entry.topGenerationId}`} className="group block">
      {plate}
    </Link>
  ) : (
    <div className="group block">{plate}</div>
  );
}

function Attribution({ entry, large }: { entry: Entry; large?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="flex items-center gap-2">
        <Identicon address={entry.address} size={18} />
        <code className="font-mono text-caption text-ink-soft">{shortAddr(entry.address)}</code>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className={`font-display font-semibold text-ink ${large ? 'text-h-section' : 'text-lede'}`}>
          {entry.score}
        </span>
        <span className="font-display text-caption italic text-ink-subtle">
          {entry.score === 1 ? 'upvote' : 'upvotes'}
        </span>
      </div>
    </div>
  );
}

/** The #1 leader — the piece itself, with a gold place medal pinned to it. */
function ChampionHero({ entry }: { entry: Entry }) {
  return (
    <section className="flex flex-col items-center gap-4 pt-9 text-center">
      <div className="relative w-44 sm:w-52">
        <GildPlate entry={entry} className="w-full" ringStrong />
        {/* Medal pinned to the top-left corner, like a ribbon on the frame. */}
        <span className="absolute -left-3 -top-3 z-10 drop-shadow-lg">
          <RankMedal place={1} size={52} />
        </span>
      </div>
      <Attribution entry={entry} large />
    </section>
  );
}

/** A runner-up (#2 / #3) — smaller plate, medal pinned. */
function RunnerUp({ entry, place }: { entry: Entry; place: 2 | 3 }) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <div className="relative w-28 sm:w-36">
        <GildPlate entry={entry} className="w-full" />
        <span className="absolute -left-2.5 -top-2.5 z-10 drop-shadow-lg">
          <RankMedal place={place} size={38} />
        </span>
      </div>
      <Attribution entry={entry} />
    </div>
  );
}

function TableRow({ entry, place }: { entry: Entry; place: number }) {
  const row = (
    <div className="grid grid-cols-[2rem_2.75rem_1fr_auto] items-center gap-3 py-3 transition-colors duration-200 group-hover:bg-surface-elevated/40">
      <span
        className="font-display text-lede italic text-ink-subtle"
        style={{ fontVariantNumeric: 'oldstyle-nums' }}
      >
        {place}
      </span>
      <div className="card-plate h-11 w-11 overflow-hidden rounded-xs">
        {entry.image ? (
          <GildedImage src={entry.image} alt="" className="block h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-surface-sunken">
            <Identicon address={entry.address} size={24} />
          </div>
        )}
      </div>
      <code className="font-mono text-micro text-ink-soft">{shortAddr(entry.address)}</code>
      <span className="font-display text-micro text-ink">
        {entry.score}
        <span className="ml-1 font-display text-caption italic text-ink-subtle">
          {entry.score === 1 ? 'vote' : 'votes'}
        </span>
      </span>
    </div>
  );
  return entry.topGenerationId ? (
    <Link to={`/result/${entry.topGenerationId}`} className="group block px-2">
      {row}
    </Link>
  ) : (
    <div className="group block px-2">{row}</div>
  );
}

function EmptySeason() {
  return (
    <section className="relative py-16">
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        {/* The empty podium: three museum plates at podium heights, gilded
            numerals waiting to be claimed. First place catches a slow sheen.
            Pure plate-and-serif vocabulary — the page's own furniture. */}
        <div className="mb-8 flex items-end justify-center gap-3.5" aria-hidden="true">
          <div
            className="podium-rise card-plate relative flex h-[5.4rem] w-[4.75rem] items-center justify-center overflow-hidden"
            style={{ '--podium-delay': '260ms' } as CSSProperties}
          >
            <span
              className="numeral-glint font-display text-[26px] font-black opacity-50"
              style={
                {
                  ...gildedNumeral(
                    'linear-gradient(115deg,#8a6020 0%,#e9c873 40%,#f3dc9a 50%,#a87f2c 62%,#8a6020 100%)',
                  ),
                  '--glint-delay': '880ms',
                } as CSSProperties
              }
            >
              II
            </span>
            <span
              className="rule-grow absolute inset-x-3.5 bottom-2.5 h-px"
              style={
                {
                  background:
                    'linear-gradient(90deg,transparent,rgb(201 165 70 / 0.33),transparent)',
                  '--rule-delay': '1100ms',
                } as CSSProperties
              }
            />
          </div>
          <div
            className="podium-rise card-plate relative flex h-[7.25rem] w-[5.5rem] items-center justify-center overflow-hidden"
            style={
              {
                borderColor: 'rgb(201 165 70 / 0.5)',
                '--podium-delay': '500ms',
              } as CSSProperties
            }
          >
            {/* Lacquered finish, top plate only: a soft inner glow, a static
                top-light gloss, and a wide feathered specular that rakes
                slowly across and fades — premium, never a hard scanning band. */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(60% 45% at 50% 42%, rgb(233 200 115 / 0.16), transparent 70%)',
              }}
            />
            <span
              aria-hidden
              className="podium-spec pointer-events-none absolute -left-[60%] -top-[30%] h-[160%] w-[60%]"
              style={{
                background:
                  'radial-gradient(50% 50% at 50% 50%, rgb(255 247 224 / 0.16), rgb(255 247 224 / 0.05) 45%, transparent 72%)',
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'linear-gradient(157deg, rgb(255 247 224 / 0.13) 0%, rgb(255 247 224 / 0.03) 18%, transparent 38%)',
              }}
            />
            <span
              className="numeral-gold relative z-10 font-display text-[36px] font-black"
              style={
                {
                  ...gildedNumeral(
                    'linear-gradient(115deg,#a87f2c 0%,#e9c873 38%,#fff3ce 50%,#c2922f 64%,#8a6020 100%)',
                  ),
                  '--glint-delay': '1150ms',
                } as CSSProperties
              }
            >
              I
            </span>
            <span
              className="rule-grow absolute inset-x-3.5 bottom-2.5 h-px"
              style={
                {
                  background:
                    'linear-gradient(90deg,transparent,rgb(233 200 115 / 0.65),transparent)',
                  '--rule-delay': '1400ms',
                } as CSSProperties
              }
            />
          </div>
          <div
            className="podium-rise card-plate relative flex h-[4.25rem] w-[4.5rem] items-center justify-center overflow-hidden"
            style={{ '--podium-delay': '80ms' } as CSSProperties}
          >
            <span
              className="numeral-glint font-display text-[22px] font-black opacity-40"
              style={
                {
                  ...gildedNumeral(
                    'linear-gradient(115deg,#8a6020 0%,#e9c873 40%,#f3dc9a 50%,#a87f2c 62%,#8a6020 100%)',
                  ),
                  '--glint-delay': '700ms',
                } as CSSProperties
              }
            >
              III
            </span>
            <span
              className="rule-grow absolute inset-x-3 bottom-2 h-px"
              style={
                {
                  background:
                    'linear-gradient(90deg,transparent,rgb(201 165 70 / 0.25),transparent)',
                  '--rule-delay': '950ms',
                } as CSSProperties
              }
            />
          </div>
        </div>

        <h2 className="heading-section">This season is just beginning</h2>
        <p className="mt-3 font-display text-lede italic text-ink-soft">
          No gildings have been published this week yet. The folio is open, the
          quill is rested. Be the first name in the margin.
        </p>

        {/* No ornament rule here: the app footer's colophon is right below,
            and two stacked dividers read as a mistake. */}
        <p className="mt-7 text-caption text-ink-subtle">
          Prizes are paid in leaves at the week boundary: 80 / 40 / 20 to
          the top three.
        </p>
      </div>
    </section>
  );
}

function LoadingState() {
  return (
    <section className="py-20" aria-live="polite">
      <div className="sweep mx-auto h-1 w-40 rounded-full bg-surface-sunken" />
      <p className="mt-6 text-center font-display italic text-ink-subtle">
        Tallying the week&rsquo;s leaves&hellip;
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------
   Page
------------------------------------------------------------------ */

export function Leaderboard() {
  const lbQuery = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => api.leaderboard(),
  });

  const entries: Entry[] = lbQuery.data?.entries ?? [];
  const weekStart = lbQuery.data?.weekStart;

  const podium = entries.slice(0, 3);
  const rest = entries.slice(3, 10);

  const formattedWeek = weekStart
    ? new Date(weekStart).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="flex flex-col">
      {/* Page signature — bespoke opening */}
      <Reveal as="header" className="pb-10 pt-2">
        <span className="eyebrow">
          {formattedWeek ? `Week of ${formattedWeek}` : 'This week'}
        </span>
        <h1 className="heading-display mt-3">The week in gildings</h1>
        <p className="mt-3 max-w-xl font-display text-lede italic text-ink-soft">
          Vote-weighted ranking. Prizes are paid in leaves at the week
          boundary; the highest place belongs to the work that earned it.
        </p>
      </Reveal>

      <div className="gild-rule" />

      {lbQuery.isLoading ? (
        <LoadingState />
      ) : entries.length === 0 ? (
        <EmptySeason />
      ) : (
        <>
          {/* The champion's piece, crowned and wreathed. */}
          {podium[0] ? (
            <Reveal delay={120}>
              <ChampionHero entry={podium[0]} />
            </Reveal>
          ) : null}

          {/* Runners-up — silver & bronze, the actual pieces beneath. */}
          {(podium[1] || podium[2]) && (
            <section className="mx-auto mt-12 grid w-full max-w-md grid-cols-2 gap-8">
              {podium[1] ? (
                <Reveal delay={80}>
                  <RunnerUp entry={podium[1]} place={2} />
                </Reveal>
              ) : (
                <div />
              )}
              {podium[2] ? (
                <Reveal delay={160}>
                  <RunnerUp entry={podium[2]} place={3} />
                </Reveal>
              ) : (
                <div />
              )}
            </section>
          )}

          {rest.length > 0 ? (
            <>
              <div className="mt-12 colophon-rule">
                <Fleuron className="h-3 w-3" />
              </div>
              <section className="mt-8">
                <h2 className="heading-section mb-2">The rest of the field</h2>
                <p className="font-display text-caption italic text-ink-subtle">
                  Ranks four through ten: the chase pack.
                </p>
                <ol className="mt-4 divide-y divide-hairline/70 border-y border-hairline/60">
                  {rest.map((e, i) => (
                    <Reveal as="li" key={e.address} delay={i * 50}>
                      <TableRow entry={e} place={i + 4} />
                    </Reveal>
                  ))}
                </ol>
              </section>
            </>
          ) : null}

          <p className="mt-12 text-center text-caption text-ink-subtle">
            Updated continuously. Final tally locks at week boundary.
          </p>
        </>
      )}
    </div>
  );
}
